import {useNotification} from '@hconnect/uikit/src/common'
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {AxiosError} from 'axios'
import React from 'react'
import {useErrorHandler} from 'react-error-boundary'

import {ErrorResponse} from '../../common/types'
import {notifyIfErrorMessage} from '../../common/utils/errorHandling'

export const settingsQueryClient = new QueryClient()

export const QueryProvider = ({children}: {children: React.ReactNode}) => {
  const {notify} = useNotification()
  const raiseError = useErrorHandler()

  const onQueryError = (error, query) => {
    const throwOnError = query.options?.throwOnError ?? true

    if (!throwOnError) return

    const errorMessage = query.meta?.errorMessage
    const axiosError = error as AxiosError<ErrorResponse>
    const errorDetails =
      axiosError?.response?.data.detail ?? axiosError.message ?? axiosError.response?.status

    if (errorMessage) {
      notifyIfErrorMessage(`${errorMessage} ${errorDetails}`, raiseError, notify)
      return
    }

    onError(errorDetails)
  }

  const onError = (error) => {
    const axiosError = error as AxiosError<Record<string, string>>
    const response = axiosError.response

    if (!response) {
      raiseError(error)
      return
    }

    const statusCode = response?.status
    const statusText = response?.data?.status ?? response?.statusText
    const url = response?.request?.responseURL

    const message = [
      `Error occurred querying ${url}`,
      `${statusCode}: ${statusText}`,
      response?.data?.detail
    ].join('\n')
    raiseError(new Error(message))
  }

  settingsQueryClient.getQueryCache().config.onError = onQueryError

  settingsQueryClient.setDefaultOptions({
    queries: {
      retry: 3,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    },
    mutations: {onError}
  })

  return (
    <QueryClientProvider client={settingsQueryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  )
}
